import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes.ts'

import { useFlights } from 'entities/tour/hooks'
import { selectionStore } from 'entities/tour/models'
import tourList from 'entities/tour/models/tour-list/tour-list'
import { setSettingForApp } from 'entities/user/module/app-settings'
import userStore from 'entities/user/module/user-store'

import { ShareKeys } from 'shared/constants'
import ImageBag from 'shared/images/bag.svg'
import { getRawColor } from 'shared/lib/getRawColor.ts'
import { Button } from 'shared/ui/button'
import { EmptyResults } from 'shared/ui/empty-result'
import { HeaderGoBack } from 'shared/ui/header-go-back'

import {
  FlightDetailsBlock,
  RoomButton,
  RoomCarousel,
  RoomDescription,
  RoomServices,
  RoomTitle,
} from './components'
import s from './styles.module.scss'

const HotelRoomDetailsPage = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const hotelDetail = selectionStore.getSelectedHotel()
  const roomDetail = selectionStore.getSelectedRoom()

  const {
    flights,
    rawFlights,
    isLoading: isFlightsLoading,
    error: isFlightsError,
  } = useFlights(roomDetail?.id, roomDetail?.operator_key)

  const displayFlight = flights?.[0] ?? null

  useEffect(() => {
    if (hotelDetail != null && roomDetail != null) return

    const raw = userStore.getClientStateParse<{
      hotel?: Parameters<typeof selectionStore.setSelectedHotel>[0]
      room?: Parameters<typeof selectionStore.setSelectedRoom>[0]
      hotelSearchSessionId?: string
    }>(ShareKeys.hotel)
    if (
      !raw ||
      typeof raw !== 'object' ||
      !raw.hotel ||
      !raw.room ||
      !raw.hotelSearchSessionId
    )
      return

    selectionStore.setSelectedHotel(raw.hotel)
    selectionStore.setSelectedRoom(raw.room)
    tourList.setUid(raw.hotelSearchSessionId)
    void setSettingForApp({ key: ShareKeys.hotel, value: '' })
  }, [hotelDetail, roomDetail, userStore.client_state])

  const handleGoBack = () => {
    const hasPreviousPages =
      window.history.length > 1 &&
      (document.referrer.includes(window.location.origin) ||
        window.history.length > 2)

    if (hasPreviousPages) {
      navigate(-1)
    } else {
      navigate(
        `${RoutingPaths.tours.tourDetail.path(hotelDetail?.hotel_id || '')}`,
      )
    }
  }

  const goToGallery = () => {
    navigate(RoutingPaths.tours.roomGallery.path(), {
      state: {
        images: roomDetail?.room_info.rooms_images || [],
        title: roomDetail?.room || '',
      },
    })
  }

  useEffect(() => {
    if (rawFlights?.length > 0) {
      selectionStore.setSelectedFlight(rawFlights[0])
    }
  }, [rawFlights])

  return (
    <div>
      <HeaderGoBack
        variant={'grey'}
        title={hotelDetail?.hotel_name || ''}
        arrowColor={getRawColor('--black')}
        onGoBack={handleGoBack}
        buttonWhite
      />

      {roomDetail ? (
        <div className={s.container}>
          <div>
            <RoomCarousel
              images={roomDetail.room_info.rooms_images || []}
              onClick={goToGallery}
            />
            <RoomTitle title={roomDetail.room} />
          </div>
          <RoomDescription description={roomDetail.room_info.descr} />

          {!isFlightsLoading && !isFlightsError && displayFlight && (
            <FlightDetailsBlock flight={displayFlight} />
          )}

          <RoomServices services={roomDetail.room_info.rooms_services} />

          <RoomButton />
        </div>
      ) : (
        <div className={s.empty}>
          <EmptyResults
            image={
              <img
                src={ImageBag}
                alt="bag image"
                className={s.image}
              />
            }
            title={t('tours_block.not_found')}
            subTitle={t('tours_block.empty_room')}
            button={
              <Button
                className={s.backButton}
                onClick={handleGoBack}
              >
                {t('tours_block.back')}
              </Button>
            }
          />
        </div>
      )}
    </div>
  )
})

export default HotelRoomDetailsPage
